<template>
  <div :class="['uiux-home-page', { 'locale-ja': localeValue === 'ja' }]">
    <section class="hero">
      <div class="inner">
        <SIBreadcrumb
          :breadcrumb="pr.common.breadcrumb"
          :locale="localeValue"
          :show-products="false"
          :show-si="false"
          :current="pr.heroTitle"
        />
        <h1>{{ pr.heroTitle }}</h1>
        <p class="hero-subtitle">{{ pr.heroSubtitle }}</p>
        <p class="hero-desc">{{ pr.heroDesc }}</p>
      </div>
    </section>

    <section class="intro">
      <div class="inner narrow">
        <p
          v-for="(line, index) in pr.intro"
          :key="`intro-${index}`"
          v-viewportChanged="showView"
          class="hidden-element"
        >
          {{ line }}
        </p>
      </div>
    </section>

    <section class="services">
      <div class="inner">
        <SISectionTitle
          :title="pr.servicesTitle"
          :subtitle="pr.servicesTitleEn"
          :subdesc="pr.servicesTitleDesc"
        />
        <div class="hidden-element services-grid" v-viewportChanged="showView">
          <article v-for="item in pr.services" :key="item.title" class="services-card">
            <div class="icon-wrap">
              <img :src="getImageUrl(item.icon)" :alt="item.title" />
            </div>
            <h3 v-viewportChanged="showView">{{ item.title }}</h3>
            <p class="title-en" v-viewportChanged="showView">{{ item.titleEn }}</p>
            <p v-viewportChanged="showView">{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="services solutions">
      <div class="inner">
        <SISectionTitle
          :title="pr.solutionsTitle"
          :subtitle="pr.solutionsTitleEn"
          :subdesc="pr.solutionsDesc"
        />
        <div class="hidden-element services-grid" v-viewportChanged="showView">
          <article v-for="item in pr.solutions" :key="item.title" class="services-card">
            <div class="icon-wrap">
              <img :src="getImageUrl(item.icon)" :alt="item.title" />
            </div>
            <h3>{{ item.title }}</h3>
            <p class="title-en">{{ item.titleEn }}</p>
            <p >{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="works">
      <div class="inner">
        <Works :isLight="false" />
      </div>
    </section>

    <section class="learn-more-fenrir inner">
      <SISectionTitle :title="pr.common.cta" />
      <BaseButton tag="router-link" variant="dark" size="hero" :to="contactLink">{{
        pr.contact
      }}</BaseButton>
    </section>
  </div>
</template>

<script>
import { computed, inject } from 'vue';
import BaseButton from '@/components/common/BaseButton.vue';
import SIBreadcrumb from '@/components/si/SIBreadcrumb.vue';
import SISectionTitle from '@/components/si/SISectionTitle.vue';
import Works from '@/components/works/UIWorks.vue';
import useRenderData from '@/composables/useRenderData';

export default {
  name: 'uiux-page',
  components: {
    BaseButton,
    SIBreadcrumb,
    SISectionTitle,
    Works,
  },
  setup() {
    const { pr, getImageUrl } = useRenderData('uiux');
    const locale = inject('locale');
    const localeValue = computed(() => locale?.value || '');
    const contactLink = computed(() => ({
      name: 'contact-us',
      params: {
        lang: localeValue.value || undefined,
      },
    }));

    return {
      pr,
      getImageUrl,
      localeValue,
      contactLink,
    };
  },
};
</script>

<style lang="scss" scoped>
.uiux-home-page {
  .inner {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    box-sizing: border-box;
  }

  .hero {
    background: #222222;
    padding: 130px 0 72px;

    h1 {
      margin: 28px 0 0;
      font-size: 32px;
      line-height: 44px;
      color: #ffffff;
      font-weight: 700;
    }

    .hero-subtitle {
      margin: 8px 0 0;
      color: #a7aeb1;
      font-size: 16px;
      line-height: 24px;
    }

    .hero-desc {
      margin: 18px 0 0;
      max-width: 760px;
      color: rgba(255, 255, 255, 0.88);
      font-size: 16px;
      line-height: 28px;
    }
  }

  .narrow {
    max-width: 800px;
  }

  .intro {
    padding: 84px 0 102px;

    p {
      margin: 0 0 16px;
      font-size: 16px;
      line-height: 30px;
      color: #212121;
      text-align: center;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .services {
    padding: 84px 0;

    .services-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 24px;
    }

    .services-card {
      border: 1px solid #e4e9eb;
      background: #ffffff;
      padding: 24px;
      display: flex;
      flex-direction: column;
      transition: transform 300ms cubic-bezier(0.165, 0.84, 0.44, 1),
        box-shadow 300ms cubic-bezier(0.165, 0.84, 0.44, 1);

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.1);
      }

      .icon-wrap {
        width: 56px;
        height: 56px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 9999px;
        background: #f1f3f4;
        align-self: center;

        img {
          width: 24px;
          height: 24px;
          object-fit: contain;
        }
      }

      h3 {
        margin: 16px 0 8px;
        color: #212121;
        font-size: 18px;
        line-height: 28px;
        font-weight: 700;
      }

      p {
        margin: 0;
        color: #666666;
        font-size: 14px;
        line-height: 24px;
        flex: 1;
      }

      .title-en {
        font-size: 16px;
        flex: 0;
        margin-bottom: 8px;
      }
    }
  }

  .solutions {
    padding-bottom: 102px;

    .inner {
      margin-bottom: 82px;
    }
  }

  .works {
    background: #f7f9fa;
  }

  .learn-more-fenrir {
    margin-top: 102px;
    margin-bottom: 84px;
    text-align: center;
  }
}

.uiux-home-page.locale-ja {
  h1,
  h2,
  h3,
  p,
  a,
  span,
  strong {
    word-break: normal;
    overflow-wrap: break-word;
    line-break: strict;
  }
}

@media only screen and (max-width: 1024px) {
  .uiux-home-page {
    .services {
      .services-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
  }
}

@media only screen and (max-width: 768px) {
  .uiux-home-page {
    .inner {
      padding: 0 12px;
    }

    .hero {
      padding: 108px 0 54px;

      h1 {
        margin-top: 18px;
        font-size: 28px;
        line-height: 38px;
      }

      .hero-desc {
        font-size: 14px;
        line-height: 24px;
      }
    }

    .services {
      padding: 60px 0;

      .services-grid {
        grid-template-columns: 1fr;
        gap: 14px;
      }
    }
  }
}
</style>
